import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, ArrowLeft, Shield, Key, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useUser.jsx';
import { apiUrl } from '../config.js';

/**
 * Test Page
 * 
 * Initiate a SAML or OIDC test - styled to match main IDNTY website
 */
export function TestPage() {
  const { configId } = useParams();
  const navigate = useNavigate();
  const { fetchWithUser, userId } = useApi();
  
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  React.useEffect(() => {
    if (userId) {
      loadConfig();
    }
  }, [configId, userId]);
  
  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await fetchWithUser(`/api/configs/${configId}`);
      if (!response.ok) throw new Error('Configuration not found');
      const data = await response.json();
      setConfig(data.config);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const startTest = async () => {
    try {
      setLoading(true);
      
      // Determine the test URL based on protocol
      // Use apiUrl to get the correct worker URL in production
      const testPath = config.type === 'saml'
        ? `/saml/login/${configId}`
        : `/oidc/authorize/${configId}`;
      const testUrl = apiUrl(testPath);
      
      // Redirect to the test endpoint
      window.location.href = testUrl;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card-heavy p-6 text-center bg-white">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
          <h2 className="font-semibold mb-2 text-lg">Error</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => navigate('/configs')}
            className="btn btn-outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Configurations
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      <button
        onClick={() => navigate('/configs')}
        className="btn btn-ghost btn-sm mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </button>
      
      <div className="card-heavy bg-white">
        <div className="card-header border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-lg bg-primary/10 text-primary">
              {config.type === 'saml' ? (
                <Key className="h-6 w-6" />
              ) : (
                <Shield className="h-6 w-6" />
              )}
            </div>
            <div>
              <h2 className="card-title">{config.name}</h2>
              <p className="text-sm text-muted-foreground">
                <span className="badge bg-primary/10 text-primary border-primary/20">
                  {config.type.toUpperCase()}
                </span>
                {' '}Test
              </p>
            </div>
          </div>
        </div>
        
        <div className="card-content space-y-4 pt-6">
          {config.description && (
            <p className="text-muted-foreground">{config.description}</p>
          )}
          
          <div className="bg-muted/50 rounded-lg p-4 border border-border">
            <h3 className="font-medium mb-3 text-primary">Test Details</h3>
            <dl className="space-y-3 text-sm">
              {config.type === 'oidc' && (
                <>
                  <div className="flex justify-between items-start">
                    <dt className="text-muted-foreground">Issuer:</dt>
                    <dd className="font-mono text-xs text-right max-w-[60%] break-all">{config.config?.provider?.issuer}</dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-muted-foreground">Client ID:</dt>
                    <dd className="font-mono text-xs">{config.config?.client?.clientId}</dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-muted-foreground">PKCE:</dt>
                    <dd className="badge bg-green-100 text-green-700 border-green-200">
                      {config.config?.security?.usePkce ? 'Enabled' : 'Disabled'}
                    </dd>
                  </div>
                  <div className="flex justify-between items-start">
                    <dt className="text-muted-foreground">Redirect URI:</dt>
                    <dd className="font-mono text-xs text-right max-w-[60%] break-all">{config.config?.client?.redirectUri}</dd>
                  </div>
                </>
              )}
              {config.type === 'saml' && (
                <>
                  <div className="flex justify-between items-start">
                    <dt className="text-muted-foreground">SP Entity ID:</dt>
                    <dd className="font-mono text-xs text-right max-w-[60%] break-all">{config.config?.sp?.entityId}</dd>
                  </div>
                  <div className="flex justify-between items-start">
                    <dt className="text-muted-foreground">ACS URL:</dt>
                    <dd className="font-mono text-xs text-right max-w-[60%] break-all">{config.config?.sp?.acsUrl}</dd>
                  </div>
                  <div className="flex justify-between items-start">
                    <dt className="text-muted-foreground">IdP SSO URL:</dt>
                    <dd className="font-mono text-xs text-right max-w-[60%] break-all">{config.config?.idp?.ssoUrl}</dd>
                  </div>
                  <div className="flex justify-between items-start">
                    <dt className="text-muted-foreground">IdP Entity ID:</dt>
                    <dd className="font-mono text-xs text-right max-w-[60%] break-all">{config.config?.idp?.entityId}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>
          
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm">
            <p className="text-foreground">
              <strong className="text-primary">Note:</strong> Clicking "Start Test" will redirect you to your configured 
              Identity Provider. After authentication, you will be redirected back to view the results.
            </p>
          </div>
        </div>
        
        <div className="card-footer justify-end border-t border-border pt-4">
          <button
            onClick={startTest}
            className="btn btn-gold"
          >
            <Play className="h-4 w-4 mr-2" />
            Start Test
          </button>
        </div>
      </div>
    </div>
  );
}
